import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    max: { type: Number, default: 120 }
  }

  connect() {
    this.frameId = null
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.handleScroll = this.handleScroll.bind(this)
    this.update = this.update.bind(this)

    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleScroll)

    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener("change", this.handleScroll)
    } else {
      this.reducedMotion.addListener(this.handleScroll)
    }

    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleScroll)

    if (this.reducedMotion.removeEventListener) {
      this.reducedMotion.removeEventListener("change", this.handleScroll)
    } else {
      this.reducedMotion.removeListener(this.handleScroll)
    }

    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId)
    }
  }

  handleScroll() {
    if (this.frameId) return

    this.frameId = window.requestAnimationFrame(this.update)
  }

  update() {
    this.frameId = null
    const wordState = this.wordState()

    if (this.reducedMotion.matches) {
      this.setParallax(0, wordState.opacity, 0)
      return
    }

    const rect = this.element.getBoundingClientRect()
    const distance = Math.max(-rect.top, 0)
    const base = Math.min(this.maxValue, distance * 0.2)

    this.setParallax(base, wordState.opacity, wordState.flash)
  }

  wordState() {
    const about = this.element.querySelector("#about")
    if (!about) return { opacity: 1, flash: 0 }

    const aboutTop = about.getBoundingClientRect().top
    const fadeStart = window.innerHeight * 0.64
    const fadeEnd = window.innerHeight * -0.54
    const progress = this.clamp((fadeStart - aboutTop) / (fadeStart - fadeEnd))
    const baseOpacity = this.fadeOpacity(progress)
    const flash = this.trailingPulse(progress, 0.48, 0.38, 0.18, 0.52, 0.3)
    const opacity = Math.max(baseOpacity, flash * 0.48)

    return { opacity: this.clamp(opacity), flash }
  }

  fadeOpacity(progress) {
    return Math.pow(1 - progress, 0.58)
  }

  smoothPulse(value, center, width) {
    const pulse = this.clamp(1 - Math.abs(value - center) / width)

    return pulse * pulse * (3 - 2 * pulse)
  }

  trailingPulse(value, center, leadWidth, decayWidth, trailWidth, trailStrength) {
    const width = value <= center ? leadWidth : decayWidth
    const pulse = this.smoothPulse(value, center, width)
    if (value <= center) return pulse

    const trail = Math.pow(this.clamp(1 - (value - center) / trailWidth), 1.35) * trailStrength

    return Math.max(pulse, trail)
  }

  clamp(value) {
    return Math.max(0, Math.min(1, value))
  }

  setParallax(base, opacity, flash) {
    this.element.style.setProperty("--parallax-word-y", `${(base * 1.06).toFixed(2)}px`)
    this.element.style.setProperty("--parallax-word-opacity", opacity.toFixed(3))
    this.element.style.setProperty("--parallax-word-flash", flash.toFixed(3))
    this.element.style.setProperty("--parallax-mark-y", `${(base * -0.2).toFixed(2)}px`)
    this.element.style.setProperty("--parallax-orbit-y", `${(base * 0.22).toFixed(2)}px`)
  }
}
